import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import { 
  Mic,
  Stop,
  Delete,
  Send,
  Person,
  Computer
} from '@mui/icons-material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { generateContent } from '../../utils/ai';

function LiveChat() {
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m your AI assistant. Start speaking, and I\'ll respond in real-time.',
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const messagesEndRef = useRef(null);

  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    initializeSpeechRecognition();
  }, [selectedLanguage]);

  const initializeSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || 
                             window.webkitSpeechRecognition || 
                             window.mozSpeechRecognition || 
                             window.msSpeechRecognition;

    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = selectedLanguage;

        recognition.onstart = () => {
          setIsRecording(true);
          setError('');
        };

        recognition.onend = () => {
          setIsRecording(false);
        };

        recognition.onresult = (event) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
              handleUserMessage(transcript);
            } else {
              interimTranscript += transcript;
            }
          }
        };

        recognition.onerror = (event) => {
          if (event.error === 'not-allowed') {
            setError('Microphone access denied. Please allow microphone access in your browser settings.');
          } else if (event.error === 'no-speech') {
            setError('No speech detected. Please try speaking again.');
          } else {
            setError(`Error recording: ${event.error}`);
          }
          setIsRecording(false);
        };

        setRecognition(recognition);
        setError('');
      } catch (err) {
        setError('Failed to initialize speech recognition. Please try a different browser.');
        console.error('Speech Recognition Init Error:', err);
      }
    } else {
      setError('Speech recognition is not supported in your browser. Please try Chrome, Edge, or Safari.');
    }
  };

  const startRecording = () => {
    if (!recognition) {
      initializeSpeechRecognition();
      return;
    }

    try {
      recognition.start();
      setError('');
    } catch (err) {
      setError('Failed to start recording. Please refresh the page and try again.');
      console.error('Start Recording Error:', err);
    }
  };

  const stopRecording = () => {
    if (recognition) {
      try {
        recognition.stop();
      } catch (err) {
        setError('Failed to stop recording. Please refresh the page and try again.');
        console.error('Stop Recording Error:', err);
      }
    }
  };

  const enhanceResponse = (text) => {
    let enhanced = text.replace(/^(.*?[.!?])(\s|$)/, '## $1\n\n');
    
    const termsToEmbolden = [
      'important', 'key', 'significant', 'crucial', 'essential',
      'note', 'remember', 'notably', 'specifically', 'particularly'
    ];
    
    const pattern = new RegExp(`\\b(${termsToEmbolden.join('|')})\\b`, 'gi');
    enhanced = enhanced.replace(pattern, '**$1**');
    
    enhanced = enhanced.replace(/(?:^|\n)[-•]\s+([^\n]+)/gm, '\n* $1');
    enhanced = enhanced.replace(/"([^"]+)"/g, '_"$1"_');
    enhanced = enhanced.replace(/`([^`]+)`/g, '`$1`');
    
    return enhanced;
  };

  const handleUserMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const messages = [...messages, userMessage].map(msg => ({ 
        role: msg.role, 
        content: msg.content 
      }));

      const response = await generateContent(messages, { max_tokens: 1000, temperature: 0.7 });

      const aiMessage = {
        role: 'assistant',
        content: enhanceResponse(response),
        timestamp: new Date().toLocaleTimeString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to get response';
      setError(errorMessage);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel id="language-label">Language</InputLabel>
          <Select
            labelId="language-label"
            id="language-select"
            value={selectedLanguage}
            label="Language"
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            {languages.map((language) => (
              <MenuItem key={language.code} value={language.code}>
                {language.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        bgcolor: 'background.default'
      }}>
        <List sx={{ width: '100%' }}>
          {messages.map((message, index) => (
            <ListItem
              key={index}
              sx={{
                flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 1,
                mb: 2,
                px: 0
              }}
              disableGutters
            >
              <ListItemAvatar>
                <Avatar 
                  sx={{ 
                    bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                    boxShadow: 1
                  }}
                >
                  {message.role === 'user' ? <Person /> : <Computer />}
                </Avatar>
              </ListItemAvatar>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  maxWidth: '70%',
                  bgcolor: message.role === 'user' 
                    ? 'primary.main' 
                    : (theme) => theme.palette.mode === 'dark' 
                      ? 'background.paper' 
                      : '#f5f5f5',
                  color: message.role === 'user' 
                    ? 'primary.contrastText' 
                    : 'text.primary',
                  borderRadius: 2,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    ...(message.role === 'user'
                      ? {
                          borderWidth: '8px 0 8px 12px',
                          borderColor: `transparent transparent transparent ${message.role === 'user' ? '#1976d2' : '#f5f5f5'}`,
                          right: '-10px',
                          top: '15px'
                        }
                      : {
                          borderWidth: '8px 12px 8px 0',
                          borderColor: 'transparent #f5f5f5 transparent transparent',
                          left: '-10px',
                          top: '15px'
                        })
                  }
                }}
              >
                {message.role === 'user' ? (
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {message.content}
                  </Typography>
                ) : (
                  <ReactMarkdown
                    components={{
                      h2: ({...props}) => <Typography variant="h6" color="primary" gutterBottom {...props} />,
                      p: ({...props}) => <Typography variant="body1" sx={{ mb: 1 }} {...props} />,
                      strong: ({...props}) => <Box component="strong" sx={{ color: 'primary.main' }} {...props} />,
                      em: ({...props}) => <Box component="em" sx={{ color: 'secondary.main' }} {...props} />,
                      li: ({...props}) => <Typography component="li" sx={{ ml: 2, mb: 0.5 }} {...props} />,
                      code: ({...props}) => (
                        <Box component="code" sx={{ bgcolor: 'grey.100', p: 0.5, borderRadius: 1 }} {...props} />
                      )
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    mt: 1, 
                    textAlign: 'right',
                    color: message.role === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                  }}
                >
                  {message.timestamp}
                </Typography>
              </Paper>
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError('')}
          sx={{ m: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={isRecording ? "Stop Recording" : "Start Recording"}>
            <IconButton 
              color={isRecording ? "error" : "primary"}
              onClick={isRecording ? stopRecording : startRecording}
              size="large"
            >
              {isRecording ? <Stop /> : <Mic />}
            </IconButton>
          </Tooltip>
          {isRecording && (
            <Typography variant="body2" color="error">
              Recording...
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default LiveChat;
